import React from 'react';
import styled from 'styled-components';
import { TChartType } from '@/features/chart-configuration/apiSlice';
import ColumnName from '@/util/ColumnName';
import { move, remove, update } from 'ramda';
import {
  Configuration,
  ConfigurationProps,
} from '@/features/chart-configuration/components/Configuration';
import {
  CHART_TYPE_TO_METRICS_RULE,
  REQUIRED_COMBINATIONS_RULES,
} from '@/features/chart-configuration/constants';
import {
  groupMetricsByRule,
  isNotConfigurable,
} from '@/features/chart-configuration/utils';
import { Button } from '@trevor/ui-components';

const Container = styled.div`
  display: flex;

  > span {
    width: 90px;
    padding-top: 13px;
    font-size: 14px;
  }
`;

const ButtonContainer = styled.div`
  margin-left: auto;
  margin-right: 45px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

export const MetricsConfiguration: React.FC<{
  chartType: TChartType;
  widgetChartType?: Nullable<string>;
  onChange: F<ColumnName[]>;
  widgetMetrics?: ColumnName[];
  dimensions: Maybe<ColumnName[]>;
  metricColumns: Nullable<ColumnName[]>;
  prefill: Maybe<ColumnName[]>;
  onUserInteraction?: F0;
}> = ({
  chartType,
  onChange,
  widgetMetrics,
  widgetChartType,
  dimensions,
  metricColumns,
  prefill,
  onUserInteraction,
}) => {
  const [optionsCombinations, setOptionsCombinations] = React.useState<
    ColumnName[][]
  >([]);
  const [selectedColumns, setSelectedColumns] = React.useState<ColumnName[]>(
    [],
  );
  const [maxSize, setMaxSize] = React.useState(0);
  const [currentSize, setCurrentSize] = React.useState(0);
  const [interacted, setInteracted] = React.useState(false);

  const configurationRule = CHART_TYPE_TO_METRICS_RULE[chartType];

  React.useEffect(() => {
    if (metricColumns) {
      const rule = CHART_TYPE_TO_METRICS_RULE[chartType];
      const preselected =
        !interacted && widgetMetrics?.length && widgetChartType === chartType
          ? widgetMetrics
          : prefill ||
            groupMetricsByRule(rule, metricColumns, rule.min).map(
              (option, index) => metricColumns[index],
            );

      const groupedOptions = groupMetricsByRule(
        rule,
        metricColumns,
        preselected.length,
      );

      setOptionsCombinations(groupedOptions);
      setSelectedColumns(preselected);
      setInteracted(true);
      setMaxSize(Math.min(metricColumns.length, rule.max));
      setCurrentSize(preselected.length);
    }
  }, [metricColumns, chartType]);

  React.useEffect(() => {
    if (selectedColumns) {
      onChange(selectedColumns);
    }
  }, [selectedColumns]);

  React.useEffect(() => {
    const combination = REQUIRED_COMBINATIONS_RULES[chartType];
    if (combination && dimensions) {
      const rules = combination.rules.filter(
        (c) =>
          c.dimensions.min <= dimensions.length &&
          dimensions.length <= c.dimensions.max,
      );
      if (rules && metricColumns) {
        if (rules.length === 1) {
          setMaxSize(Math.min(rules[0].metrics.max, metricColumns.length));
        } else {
          setMaxSize(metricColumns.length);
        }
      }
    }
  }, [dimensions?.length, chartType]);

  if (
    isNotConfigurable(configurationRule) ||
    !metricColumns ||
    selectedColumns.length === 0
  ) {
    return null;
  }

  const handleOptionSelect: ConfigurationProps<ColumnName>['onOptionSelect'] = (
    index,
    option,
  ) => {
    setSelectedColumns(update(index, option));
    onUserInteraction?.();
  };

  const handleReorderSelects: ConfigurationProps<ColumnName>['onReorderSelects'] =
    (sourceIndex, destinationIndex) => {
      const handleMove = move(sourceIndex, destinationIndex);

      setSelectedColumns(handleMove);
      onUserInteraction?.();
    };

  const handleSelectRemove: ConfigurationProps<ColumnName>['onSelectRemove'] = (
    index,
  ) => {
    const handleRemove = remove<any>(index, 1);

    setSelectedColumns(handleRemove);
    const newSize = currentSize - 1;
    const groupedOptions = groupMetricsByRule(
      CHART_TYPE_TO_METRICS_RULE[chartType],
      metricColumns,
      newSize,
    );
    setOptionsCombinations(groupedOptions);
    setCurrentSize(newSize);
    onUserInteraction?.();
  };

  const handleAddItem = () => {
    const newSize = currentSize + 1;
    setCurrentSize(newSize);
    const groupedOptions = groupMetricsByRule(
      CHART_TYPE_TO_METRICS_RULE[chartType],
      metricColumns,
      newSize,
    );
    setOptionsCombinations(groupedOptions);
    const items = metricColumns.find(
      (m) =>
        selectedColumns.findIndex(
          (c) => m._alias === c._alias && m._name === c._name,
        ) === -1,
    );
    if (items) {
      setSelectedColumns([...selectedColumns, items]);
      onUserInteraction?.();
    }
  };

  return (
    <>
      <Container $notRemovable>
        <span>Metrics</span>

        <Configuration
          configurableType="metrics"
          selectedOptions={selectedColumns}
          optionsCombinations={optionsCombinations}
          onOptionSelect={handleOptionSelect}
          onReorderSelects={handleReorderSelects}
          onSelectRemove={handleSelectRemove}
          min={configurationRule.min}
        />
      </Container>
      {currentSize < maxSize && (
        <ButtonContainer>
          <Button variant="secondary" onClick={handleAddItem}>
            add another item
          </Button>
        </ButtonContainer>
      )}
    </>
  );
};
